import React, { useEffect, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";
import { toast } from "react-toastify";
import GeneralLedgerService from "../../../Services/generalLedger/GeneralLedgerService";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import { formatMoney } from "../../../utils/formatMoney";
import { can, PERMISSIONS } from "./glUtils";

const EMPTY = {
  company_id: "",
  label: "",
  bank_name: "",
  bank_code: "",
  account_number: "",
  iban: "",
  currency_code: "",
  parser_template: "",
  opening_balance: "",
  is_active: true,
};

function ProfileModal({ meta, profile, onClose }) {
  const queryClient = useQueryClient();
  const isEdit = Boolean(profile);
  const [form, setForm] = useState(
    isEdit
      ? { ...EMPTY, ...profile, opening_balance: profile.opening_balance ?? "", label: profile.label ?? "" }
      : { ...EMPTY, parser_template: meta?.templates?.[0]?.key || "" }
  );
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const mutation = useMutation({
    mutationFn: () => {
      if (isEdit) {
        return GeneralLedgerService.updateBankProfile(profile.id, {
          label: form.label || null,
          bank_name: form.bank_name,
          parser_template: form.parser_template,
          opening_balance: form.opening_balance === "" ? null : form.opening_balance,
          is_active: Boolean(form.is_active),
        });
      }
      return GeneralLedgerService.createBankProfile({
        company_id: Number(form.company_id),
        label: form.label || null,
        bank_name: form.bank_name,
        bank_code: form.bank_code || null,
        account_number: form.account_number,
        iban: form.iban || null,
        currency_code: form.currency_code,
        parser_template: form.parser_template,
        opening_balance: form.opening_balance === "" ? null : form.opening_balance,
      });
    },
    onSuccess: () => {
      toast.success(isEdit ? "Bank profile updated" : "Bank profile created");
      ["glBankProfiles", "glMeta"].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
      onClose();
    },
    onError: (e) => toast.error(getApiErrorMessage(e, "Could not save the bank profile.")),
  });

  return (
    <Modal isOpen toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>{isEdit ? "Edit bank profile" : "New bank profile"}</ModalHeader>
      <ModalBody>
        <Row className="g-3">
          {!isEdit && (
            <Col md="6">
              <Label>Company</Label>
              <Input type="select" value={form.company_id} onChange={set("company_id")}>
                <option value="">Select…</option>
                {(meta?.companies || []).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Input>
            </Col>
          )}
          <Col md="6">
            <Label>Label</Label>
            <Input value={form.label} maxLength={191} onChange={set("label")} placeholder="e.g. Attijari main TND" />
          </Col>
          <Col md="6">
            <Label>Bank name</Label>
            <Input value={form.bank_name} maxLength={191} onChange={set("bank_name")} />
          </Col>
          <Col md="6">
            <Label>Statement template</Label>
            <Input type="select" value={form.parser_template} onChange={set("parser_template")}>
              {(meta?.templates || []).map((t) => (
                <option key={t.key} value={t.key}>{t.bank_name} ({t.key})</option>
              ))}
            </Input>
          </Col>
          {!isEdit && (
            <>
              <Col md="6">
                <Label>Account number</Label>
                <Input value={form.account_number} maxLength={64} autoComplete="off" onChange={set("account_number")} />
              </Col>
              <Col md="6">
                <Label>IBAN</Label>
                <Input value={form.iban} maxLength={42} autoComplete="off" onChange={set("iban")} />
              </Col>
              <Col md="3">
                <Label>Currency</Label>
                <Input value={form.currency_code} maxLength={3} onChange={(e) => setForm({ ...form, currency_code: e.target.value.toUpperCase() })} />
              </Col>
              <Col md="3">
                <Label>Bank code</Label>
                <Input value={form.bank_code} maxLength={16} onChange={set("bank_code")} />
              </Col>
            </>
          )}
          <Col md="3">
            <Label>Opening balance</Label>
            <Input type="number" step="any" value={form.opening_balance} onChange={set("opening_balance")} />
          </Col>
          {isEdit && (
            <Col md="3" className="d-flex align-items-end">
              <Label check className="d-flex gap-2 align-items-center">
                <Input type="checkbox" checked={Boolean(form.is_active)} onChange={set("is_active")} /> Active
              </Label>
            </Col>
          )}
        </Row>
        {isEdit && (
          <p className="gl-muted mt-3 mb-0">
            Account number, IBAN and currency are fixed once a profile exists, so linked statements stay consistent.
          </p>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose}>Cancel</Button>
        <Button color="primary" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
          {mutation.isPending ? "Saving…" : "Save"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function RevealButton({ profile }) {
  const [revealed, setRevealed] = useState(null);
  const [loading, setLoading] = useState(false);
  const hideTimer = useRef(null);

  useEffect(() => () => window.clearTimeout(hideTimer.current), []);

  const toggle = async () => {
    window.clearTimeout(hideTimer.current);
    if (revealed) {
      setRevealed(null);
      return;
    }
    setLoading(true);
    try {
      const res = await GeneralLedgerService.getBankProfile(profile.id, true);
      setRevealed(res.data);
      hideTimer.current = window.setTimeout(() => setRevealed(null), 30000);
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Not allowed to reveal this account."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="gl-mono">{revealed ? revealed.iban || revealed.account_number : profile.iban || profile.account_number}</div>
      <Button color="link" size="sm" className="p-0" disabled={loading} onClick={toggle}>
        {revealed ? "Hide" : "Reveal (audited)"}
      </Button>
    </div>
  );
}

export default function BankProfilesTab({ meta, onOpenLedger }) {
  const [editing, setEditing] = useState(null);
  const canManage = can(meta, PERMISSIONS.MANAGE_PROFILES);

  const profilesQuery = useQuery({
    queryKey: ["glBankProfiles"],
    queryFn: () => GeneralLedgerService.getBankProfiles().then((r) => r.data),
  });

  const columns = [
    {
      name: "Profile",
      grow: 2,
      cell: (p) => (
        <div>
          <div className="gl-cell-strong">{p.label || p.bank_name}</div>
          <div className="gl-muted">{p.bank_name} · {p.company_name}</div>
        </div>
      ),
    },
    {
      name: "Account",
      grow: 2,
      cell: (p) => (canManage ? <RevealButton profile={p} /> : <span className="gl-mono">{p.iban || p.account_number}</span>),
    },
    { name: "Currency", width: "100px", selector: (p) => p.currency_code },
    { name: "Template", width: "150px", selector: (p) => p.parser_template },
    { name: "Opening", width: "150px", right: true, cell: (p) => formatMoney(p.opening_balance, p.currency_code) },
    { name: "Statements", width: "110px", right: true, selector: (p) => p.statements_count ?? 0 },
    {
      name: "Status",
      width: "100px",
      cell: (p) => (p.is_active ? <Badge color="success">Active</Badge> : <Badge color="secondary">Inactive</Badge>),
    },
    {
      name: "",
      width: "170px",
      right: true,
      cell: (p) => (
        <div className="d-flex gap-1">
          <Button size="xs" color="light" onClick={() => onOpenLedger({ bank_profile_id: String(p.id) })}>Ledger</Button>
          {canManage && <Button size="xs" color="light" onClick={() => setEditing(p)}>Edit</Button>}
        </div>
      ),
    },
  ];

  return (
    <div>
      {canManage && (
        <div className="text-end mb-3">
          <Button color="primary" disabled={(meta?.companies || []).length === 0} onClick={() => setEditing("new")}>
            New bank profile
          </Button>
        </div>
      )}
      {profilesQuery.isError && (
        <div className="alert alert-danger d-flex align-items-center justify-content-between gap-2">
          <span>{getApiErrorMessage(profilesQuery.error, "Could not load the bank profiles.")}</span>
          <Button size="sm" color="light" onClick={() => profilesQuery.refetch()}>Try again</Button>
        </div>
      )}
      <DataTable
        columns={columns}
        data={profilesQuery.data || []}
        keyField="id"
        progressPending={profilesQuery.isLoading}
        highlightOnHover
        responsive
        noDataComponent={<div className="p-4 gl-muted">No bank profiles yet. They are created when a statement is linked.</div>}
      />
      {editing && <ProfileModal meta={meta} profile={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
